import { getFirstPodcast } from "../private/api/getFirstPodcast";
import { getPreviewPodcast } from "../private/api/getPreviewPodcast";
import { getSignedUrl } from "../private/api/getSignedUrl";
import { getOtherPodcastList } from "../private/api/otherPodcasts";

const Audioplayer = {
  namespaced: true,

  state: {
    list: {},
    otherPodcasts: {},

    bufferedRanges: [],
    audioplayer: null,

    isPlaying: false,
    isLoading: false,
    volume: 1,
    speed: 1,
    currentTime: 0,

    activePodcastId: null,
    podcastPreviewText: null,

    isAudioplayerLoading: false,
    isAudioplayerReady: false,
    isStickyPanelOpened: false,
    isLoadingPodcastsList: false,

    currentAudioRequest: null,
    wasTimeRestoredFromStorage: false,
  },

  actions: {
    async initPlayer({ commit, dispatch, state }) {
      await dispatch('initFirstPodcast');
      commit('SET_AUDIOPLAYER_API');

      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', () => dispatch('resumeAudio'));
        navigator.mediaSession.setActionHandler('pause', () => dispatch('pauseAudio'));
        navigator.mediaSession.setActionHandler('seekto', e => {
          if (e.seekTime != null) dispatch('seekTo', e.seekTime);
        });
        navigator.mediaSession.setActionHandler('seekbackward', e => {
          dispatch('seekTo', Math.max(0, state.currentTime - (e.seekOffset || 10)));
        });
        navigator.mediaSession.setActionHandler('seekforward', e => {
          dispatch(
            'seekTo',
            Math.min(state.audioplayer.duration || 0, state.currentTime + (e.seekOffset || 10))
          );
        });
      }

      commit('SET_VOLUME', parseFloat(localStorage.getItem('audio_volume')) || 0.5);
      commit('SET_SPEED', parseFloat(localStorage.getItem('audio_speed')) || 1);
      commit('SET_READY_AUDIOPLAYER_FLAG');
    },

    async initFirstPodcast({ commit }) {
      commit('SET_LOADING_AUDIOPLAYER_FLAG', true);
      const data = await getFirstPodcast();
      const podcast = data[0].podcast_atributes;
      commit('SET_NEW_AUDIO', podcast);
      commit('WRITE_ID_ACTIVE_PODCAST', podcast.id);
      commit('SET_LOADING_AUDIOPLAYER_FLAG', false);
    },

    async activeCurrentPodcats({ state, commit, dispatch }) {
      commit('TOGGLE_STICKY_PANEL', true);
      commit('SET_LOADING_AUDIO_FLAG', true);

      const podcast = state.list[state.activePodcastId];
      if (!podcast) return;

      try {
        await dispatch('ensureValidSignedUrl');

        if (state.audioplayer.src !== podcast.signed_url) {
          commit('SET_AUDIO_URL', podcast.signed_url);
          await new Promise(res => {
            state.audioplayer.oncanplay = res;
          });
        }

        state.audioplayer.currentTime = state.currentTime;
        state.audioplayer.playbackRate = state.speed;
        state.audioplayer.volume = state.volume;

        dispatch('updateMediaSessionMetadata', podcast);
        dispatch('updateMediaSessionPosition');

        await state.audioplayer.play();
        commit('SET_PLAYING_FLAG', true);
        navigator.mediaSession && (navigator.mediaSession.playbackState = 'playing');
      } finally {
        commit('SET_LOADING_AUDIO_FLAG', false);
      }
    },

    async ensureValidSignedUrl({ state, dispatch }) {
      const podcast = state.list[state.activePodcastId];
      if (!podcast) return;

      if (!podcast.signed_url || !podcast.expires_at) {
        return dispatch('useSignedUrlForAudio');
      }

      const expires = new Date(podcast.expires_at).getTime();
      if (Date.now() > expires) {
        const resumeTime = state.currentTime;
        await state.audioplayer.pause();
        await dispatch('useSignedUrlForAudio');
        state.audioplayer.currentTime = resumeTime;
      }
    },

    async useSignedUrlForAudio({ state, commit }) {
      const podcast = state.list[state.activePodcastId];
      if (!podcast?.audio_file) return;

      const name = podcast.audio_file.match(/\/([^\/?#]+)$/)?.[1];
      if (!name) return;

      const req = getSignedUrl(name);
      commit('SET_AUDIO_REQUEST', req);
      const data = await req;

      commit('UPDATE_PODCAST_SIGNED_URL', {
        id: state.activePodcastId,
        signed_url: data.signed_url.replace(/\.sdn/, ''),
        /* signed_url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3', */
        expires_at: data.expires_at,
      });
    },

    async playAudio({ commit, dispatch }, { id }) {
      commit('SET_CURRENT_TIME', 0);
      commit('WRITE_ID_ACTIVE_PODCAST', id);
      dispatch('activeCurrentPodcats');
    },

    async resumeAudio({ dispatch }) {
      dispatch('activeCurrentPodcats');
    },

    async pauseAudio({ state, commit }) {
      if (!state.audioplayer) return;
      await state.audioplayer.pause();
      commit('SET_PLAYING_FLAG', false);
      navigator.mediaSession && (navigator.mediaSession.playbackState = 'paused');
    },

    seekTo({ state, commit }, time) {
      state.audioplayer.currentTime = time;
      commit('SET_CURRENT_TIME', time);
      navigator.mediaSession?.setPositionState({
        duration: state.audioplayer.duration || 0,
        position: time,
        playbackRate: state.speed,
      });
    },

    updateMediaSessionMetadata(_, podcast) {
      if (!navigator.mediaSession) return;

      const artwork = podcast.cover_url
        ? [{ src: podcast.cover_url, sizes: '512x512', type: 'image/png' }]
        : [];

      navigator.mediaSession.metadata = new MediaMetadata({
        title: podcast.title || 'Dropsc',
        artist: podcast.author || 'Drops Capital',
        artwork,
      });
    },

    updateMediaSessionPosition({ state }) {
      navigator.mediaSession?.setPositionState({
        duration: state.audioplayer.duration || 0,
        position: state.currentTime,
        playbackRate: state.speed,
      });
    },

    async addPodcastToList({ commit }, podcast) {
      commit('SET_NEW_AUDIO', podcast);
    },
  },

  mutations: {
    SET_AUDIOPLAYER_API(state) {
      state.audioplayer = new Audio();

      state.audioplayer.ontimeupdate = () => {
        state.currentTime = state.audioplayer.currentTime;
        localStorage.setItem('last_remember_time', state.currentTime);
        navigator.mediaSession?.setPositionState({
          duration: state.audioplayer.duration || 0,
          position: state.currentTime,
          playbackRate: state.speed,
        });
      };
    },

    UPDATE_PODCAST_SIGNED_URL(state, { id, signed_url, expires_at }) {
      state.list[id].signed_url = signed_url;
      state.list[id].expires_at = expires_at;
    },

    SET_AUDIO_URL(state, url) {
      state.audioplayer.src = url;
    },

    SET_VOLUME(state, v) {
      state.volume = v;
      state.audioplayer && (state.audioplayer.volume = v);
    },

    SET_SPEED(state, s) {
      state.speed = s;
      state.audioplayer && (state.audioplayer.playbackRate = s);
    },

    SET_CURRENT_TIME(state, t) {
      state.currentTime = t;
    },

    SET_NEW_AUDIO(state, podcast) {
      state.list[podcast.id] = podcast;
    },

    WRITE_ID_ACTIVE_PODCAST(state, id) {
      state.activePodcastId = id;
    },

    SET_PLAYING_FLAG(state, v) {
      state.isPlaying = v;
    },

    TOGGLE_STICKY_PANEL(state, v) {
      state.isStickyPanelOpened = v;
    },

    SET_LOADING_AUDIO_FLAG(state, v) {
      state.isLoading = v;
    },

    SET_LOADING_AUDIOPLAYER_FLAG(state, v) {
      state.isAudioplayerLoading = v;
    },

    SET_READY_AUDIOPLAYER_FLAG(state) {
      state.isAudioplayerReady = true;
    },

    SET_AUDIO_REQUEST(state, r) {
      state.currentAudioRequest = r;
    },
  },
};

export default Audioplayer;